import { generateFullName } from '@sharedInterfaces/globalHelper';
import getSuggestedEmployees from '@src/APIs/graphQl/Opportunity/getSuggestedEmployees';
import { generateDescription, isOrgUnit } from '@src/App/Components/Buttons/ShareButton/ShareButton';
import { stageContext } from '@src/globals';
import { combinedKnowledge } from '@src/helper/helper';
import Business from '@src/Objects/Business';
import Certificate from '@src/Objects/Certificate';
import Competence from '@src/Objects/Competence';
import Employee from '@src/Objects/Employee';
import type { Entity } from '@src/Objects/Entity';
import Opportunity from '@src/Objects/Opportunity';
import Product from '@src/Objects/Product';
import Project from '@src/Objects/Project';
import { SkillCategory } from '@src/Objects/SkillCategory';
import SkillDetail from '@src/Objects/SkillDetail';
import store from '@store/store';

/**
 * Generates text for the given entity.
 *
 * @param {Entity} entity - The entity object to generate text for.
 * @returns {string} The generated text.
 */
export async function generateTextForEntity(entity: Entity, link: string): Promise<{ subject: string; text: string }> {
    if (entity instanceof Employee) {
        return {
            subject: `${stageContext.productName} - Profil: ${entity.fullName}`,
            text: `Sie dir bitte das Profil von ${entity.fullName} an.

${generateDescription(entity.description, 'Zusammenfassung')}
Link: ${link}`,
        };
    } else if (entity instanceof SkillDetail) {
        return {
            subject: `${stageContext.productName} - Fähigkeit: ${entity.title}`,
            text: `Sie dir bitte die Fähigkeit "${entity.title}" an.

${generateDescription(entity.description)}
Link: ${link}`,
        };
    } else if (entity instanceof Certificate) {
        return {
            subject: `${stageContext.productName} - Zertifikat: ${entity.title}`,
            text: `Sie dir bitte das Zertifikat "${entity.title}" an.

${generateDescription(entity.description)}
Link: ${link}`,
        };
    } else if (entity instanceof SkillCategory) {
        return {
            subject: `${stageContext.productName} - Fähigkeitskategorie: ${entity.title}`,
            text: `Sie dir bitte die Fähigkeitskategorie "${entity.title}" an.

${generateDescription(entity.description)}
Link: ${link}`,
        };
    } else if (entity instanceof Competence) {
        return {
            subject: `${stageContext.productName} - Kompetenz: ${entity.title}`,
            text: `Sie dir bitte die Kompetenz "${entity.title}" an.

${generateDescription(entity.description)}
Level:
${entity.levels.map((l) => `${l.id}. ${l.title}`).join(`\n`)}

Mitarbeiter:
${entity.employees.map((e) => `${e.level}\t -\t ${e.title}`).join(`\n`)}

Link: ${link}`,
        };
    } else if (entity instanceof Product) {
        return {
            subject: `${stageContext.productName} - Produkt: ${entity.title}`,
            text: `Sie dir bitte das Produkt "${entity.title}" an.

${generateDescription(entity.description)}
Mitarbeiter:
${entity.employees
    .filter((e) => e.match > 0)
    .map((e) => `${e.match.toString().padStart(2, '0')}%\t -\t ${e.title}`)
    .join(`\n`)}

Kompetenzen:
${entity.competences.map((l) => `${l.level}\t -\t ${l.title}`).join(`\n`)}

Fähigkeiten:
${entity.skills.map((l) => `${l.level}\t -\t ${l.title}`).join(`\n`)}

Zertifikate:
${entity.certificates.map((l) => l.title).join(`\n`)}

Link: ${link}`,
        };
    } else if (entity instanceof Opportunity) {
        const { combinedSkills, combinedCertificates, combinedRoles } = combinedKnowledge(
            entity,
            store.getState().company.allCompetences,
        );

        const suggestedEmployees = await getSuggestedEmployees(
            combinedSkills.map((s) => ({ id: s.id, title: s.title, level: s.level })),
            combinedCertificates.map((c) => ({ id: c.id, title: c.title })),
            combinedRoles.map((r) => ({ id: r.id, title: r.title })),
        );
        return {
            subject: `${stageContext.productName} - Anfrage: ${entity.title}`,
            text: `Sie dir bitte die Anfrage "${entity.title}" an.

${generateDescription(entity.requestText, 'Anfragetext')}
Ausgewählte Mitarbeiter:
${entity.selectedEmployees
    .filter((e) => e.score > 0)
    .map(
        (e) =>
            `${Math.round(e.score * 100)
                .toString()
                .padStart(2, '0')}%\t -\t ${e.title}`,
    )
    .join(`\n`)}

Vorgeschlagene Mitarbeiter:
${suggestedEmployees
    .filter((e) => e.score > 0)
    .map(
        (e) =>
            `${Math.round(e.score * 100)
                .toString()
                .padStart(2, '0')}%\t -\t ${e.title}`,
    )
    .join(`\n`)}

Kompetenzen:
${entity.competences.map((l) => `${l.level}\t -\t ${l.title}`).join(`\n`)}

Fähigkeiten:
${entity.skills.map((l) => `${l.level}\t -\t ${l.title}`).join(`\n`)}

Link: ${link}`,
        };
    } else if (isOrgUnit(entity)) {
        return {
            subject: `${stageContext.productName} - Organisationseinheit: ${entity.title}`,
            text: `Sie dir bitte die Organisationseinheit "${entity.title}" an.

Mitarbeiter:
${entity.employees.map((e) => generateFullName(e.firstname, e.lastname)).join(`\n`)}

Fähigkeitskategorien:
${entity.skillCategories.map((l) => `${l.count}\t -\t ${l.title}`).join(`\n`)}

Fähigkeiten:
${entity.skills.map((l) => `${l.count}\t -\t ${l.title}`).join(`\n`)}

Link: ${link}`,
        };
    } else if (entity instanceof Business) {
        return {
            subject: `${stageContext.productName} - Firma: ${entity.title}`,
            text: `Sie dir bitte die Firma "${entity.title}" an.

Projekte:
${entity.projects.map((l) => l.title).join(`\n`)}

Link: ${link}`,
        };
    } else if (entity instanceof Project) {
        return {
            subject: `${stageContext.productName} - Projekt: ${entity.title}`,
            text: `Sie dir bitte das Projekt "${entity.title}" an.

Kunde: ${entity.business.title}
Aktiv: ${entity.active ? 'Ja' : 'Nein'}
Stunden: ${entity.isHours} / ${entity.totalHours} - ${Math.round((entity.isHours / entity.totalHours) * 100)}%
Tagespreis: ${entity.dayPrice}
Bisheriger Umsatz: ${entity.revenue}€

Mitarbeiter:
${entity.employees.map((l) => l.title).join(`\n`)}

Link: ${link}`,
        };
    }
    return {
        subject: '${stageContext.productName}',
        text: `${entity.title} - ${link}`,
    };
}
